import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Search,
  EmojiEvents,
  TrendingUp,
  School
} from '@mui/icons-material';
import { getScores } from '../../services/adminService';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);

function ScoreManagement() {
  const [scores, setScores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadScores();
  }, []);

  const loadScores = async () => {
    setLoading(true);
    try {
      const scoreData = await getScores();
      setScores(scoreData);
    } catch (error) {
      console.error('Error loading scores:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredScores = scores.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // İstatistikler
  const totalUsers = scores.length;
  const averageScore = scores.reduce((acc, user) => acc + (user.score || 0), 0) / totalUsers;
  const highestScore = Math.max(...scores.map(user => user.score || 0));

  return (
    <Box>
      {loading ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : (
        <>
          {/* İstatistik Kartları */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <MotionCard
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                sx={{ 
                  background: 'linear-gradient(135deg, rgba(0, 191, 13, 0.1) 0%, rgba(76, 241, 61, 0.1) 100%)',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <EmojiEvents sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {totalUsers}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Toplam Kullanıcı
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <MotionCard
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                sx={{ 
                  background: 'linear-gradient(135deg, rgba(0, 191, 13, 0.1) 0%, rgba(76, 241, 61, 0.1) 100%)',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {averageScore.toFixed(0)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Ortalama Puan
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <MotionCard
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{ 
                  background: 'linear-gradient(135deg, rgba(0, 191, 13, 0.1) 0%, rgba(76, 241, 61, 0.1) 100%)',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <School sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {highestScore}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    En Yüksek Puan
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>

          {/* Arama ve Tablo */}
          <Paper sx={{ p: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sıralama</TableCell>
                    <TableCell>Kullanıcı</TableCell>
                    <TableCell>Seviye</TableCell>
                    <TableCell>Puan</TableCell>
                    <TableCell>İlerleme</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredScores.map((user, index) => {
                    const level = Math.floor((user.score || 0) / 100) + 1;
                    const progress = (user.score || 0) % 100;
                    
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {index < 3 ? (
                              <EmojiEvents sx={{ 
                                color: ['#FFD700', '#C0C0C0', '#CD7F32'][index]
                              }} />
                            ) : null}
                            #{index + 1}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {user.username?.charAt(0).toUpperCase()}
                            </Avatar>
                            {user.username}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`Seviye ${level}`}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{user.score || 0}</TableCell>
                        <TableCell sx={{ width: '30%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={progress}
                              sx={{ 
                                flexGrow: 1,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'rgba(0, 191, 13, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                                  borderRadius: 4
                                }
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {progress}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}

export default ScoreManagement; 